import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  createLiveSection,
  getLiveSection,
  getLiveSections,
  updateLiveSection
} from '@utils/api/realtime/live-sections.ts';
import { LiveDraft } from '@utils/editors/live-transcript-state.ts';
import {
  frozenSectionRows,
  sectionRows
} from '@utils/editors/live-section-state.ts';
import {
  LiveSection,
  LiveSectionAction,
  LiveSectionCreate,
  LiveSectionDetail
} from '@utils/types/editors/live-section.ts';

type SelectGuard = (detail: LiveSectionDetail) => boolean;

export const useLiveSections = (
  sessionId: string,
  scope: string,
  rows: LiveDraft[],
  canEdit: boolean,
  saveCorrections: (ids?: string[]) => Promise<boolean>,
  refreshRange: (first: number, last: number) => Promise<boolean>
) => {
  const [loadedScope, setLoadedScope] = useState('');
  const [sections, setSections] = useState<LiveSection[]>([]);
  const [selected, setSelected] = useState<LiveSectionDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<'load' | 'save' | 'conflict' | null>(null);
  const generation = useRef(0);
  const request = useRef(0);
  const writing = useRef(false);

  useEffect(() => {
    const active = ++generation.current;
    ++request.current;
    writing.current = false;
    setSections([]);
    setSelected(null);
    setBusy(false);
    setCompleting(false);
    setError(null);
    setLoadedScope(scope);
    void getLiveSections(sessionId)
      .then(value => {
        if (generation.current === active) setSections(value);
      })
      .catch(() => {
        if (generation.current === active) setError('load');
      });
    return () => {
      ++generation.current;
    };
  }, [scope, sessionId]);

  const select = useCallback(
    async (id: string, guard?: SelectGuard) => {
      if (writing.current) return;
      const active = generation.current;
      const selection = ++request.current;
      setError(null);
      if (!id) {
        setSelected(null);
        setBusy(false);
        return;
      }
      setBusy(true);
      try {
        const detail = await getLiveSection(sessionId, id);
        if (generation.current !== active || request.current !== selection)
          return;
        if (
          !detail.completed &&
          !(await refreshRange(detail.start_sequence, detail.end_sequence))
        ) {
          setError('load');
          return;
        }
        if (generation.current === active && request.current === selection) {
          if (guard && !guard(detail)) return;
          setSelected(detail);
        }
      } catch {
        if (generation.current === active && request.current === selection)
          setError('load');
      } finally {
        if (generation.current === active && request.current === selection)
          setBusy(false);
      }
    },
    [sessionId, refreshRange]
  );

  const mutate = useCallback(
    async (work: () => Promise<LiveSectionDetail | null>, complete = false) => {
      if (!canEdit || writing.current) return false;
      const active = generation.current;
      writing.current = true;
      ++request.current;
      setBusy(true);
      setCompleting(complete);
      setError(null);
      try {
        const detail = await work();
        if (generation.current !== active) return false;
        if (!detail) {
          setError('save');
          return false;
        }
        const info: LiveSection = {
          section_id: detail.section_id,
          name: detail.name,
          start_segment_id: detail.start_segment_id,
          end_segment_id: detail.end_segment_id,
          start_sequence: detail.start_sequence,
          end_sequence: detail.end_sequence,
          start_ms: detail.start_ms,
          end_ms: detail.end_ms,
          completed: detail.completed,
          revision: detail.revision,
          saved_at: detail.saved_at
        };
        setSections(previous =>
          [
            ...previous.filter(item => item.section_id !== detail.section_id),
            info
          ].sort((a, b) => a.start_sequence - b.start_sequence)
        );
        setSelected(detail);
        return true;
      } catch (failure) {
        if (generation.current === active)
          setError(
            isAxiosError(failure) && failure.response?.status === 409
              ? 'conflict'
              : 'save'
          );
        return false;
      } finally {
        if (generation.current === active) {
          writing.current = false;
          setBusy(false);
          setCompleting(false);
        }
      }
    },
    [canEdit]
  );

  const create = useCallback(
    async (body: LiveSectionCreate) =>
      mutate(async () => {
        const first = rows.find(
          row => row.segment.segment_id === body.start_segment_id
        );
        const last = rows.find(
          row => row.segment.segment_id === body.end_segment_id
        );
        if (!first || !last || first.segment.sequence > last.segment.sequence)
          return null;
        const ids = rows
          .filter(
            row =>
              row.segment.sequence >= first.segment.sequence &&
              row.segment.sequence <= last.segment.sequence
          )
          .map(row => row.segment.segment_id);
        if (!(await saveCorrections(ids))) return null;
        return createLiveSection(sessionId, body);
      }),
    [mutate, rows, saveCorrections, sessionId]
  );

  const update = useCallback(
    async (action: LiveSectionAction = 'save') => {
      if (!selected) return false;
      return mutate(async () => {
        if (
          action === 'reopen' &&
          !(await refreshRange(selected.start_sequence, selected.end_sequence))
        )
          return null;
        if (action !== 'reopen') {
          const ids = sectionRows(rows, selected).map(
            row => row.segment.segment_id
          );
          if (!(await saveCorrections(ids))) return null;
        }
        return updateLiveSection(sessionId, selected, action);
      }, action === 'complete');
    },
    [mutate, rows, saveCorrections, selected, sessionId, refreshRange]
  );

  const reload = useCallback(async () => {
    if (writing.current) return;
    const active = generation.current;
    const selection = request.current;
    try {
      const values = await getLiveSections(sessionId);
      if (generation.current !== active) return;
      setSections(values);
      setError(null);
      if (selected && request.current === selection)
        await select(selected.section_id);
    } catch {
      if (generation.current === active) setError('load');
    }
  }, [select, selected, sessionId]);

  const hydrated = loadedScope === scope;
  const current = hydrated ? selected : null;
  const visibleRows = useMemo(
    () =>
      !current
        ? rows
        : current.completed
          ? frozenSectionRows(current)
          : sectionRows(rows, current),
    [current, rows]
  );
  return {
    sections: hydrated ? sections : [],
    selected: current,
    rows: visibleRows,
    renderScope: `${scope}:${current?.completed ? `frozen:${current.section_id}:${current.revision}` : 'live'}`,
    busy,
    completing,
    error,
    select,
    create,
    update,
    reload
  };
};
